import { useSelector } from '@xstate/store-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getOwnedFieldKeys } from '../../drawerFieldKeys';
import { type UniversalDrawerFieldConfig } from '../../types';
import { useUniversalFiltersContext } from '../../UniversalFiltersProvider';

import { FiltersDrawerAddFieldList } from './FiltersDrawerAddFieldList';
import { FiltersDrawerAddFieldMenu } from './FiltersDrawerAddFieldMenu';
import { FiltersDrawerConfiguredRow } from './FiltersDrawerConfiguredRow';

export function FiltersDrawerBody() {
  const { config, store } = useUniversalFiltersContext();
  const { t } = useTranslation('universalFilters');
  const [pendingFocusField, setPendingFocusField] = useState<string | null>(
    null,
  );

  const drawerFields = useMemo(
    () => config.drawerFields ?? [],
    [config.drawerFields],
  );
  const fieldConfigByField = useMemo(
    () =>
      new Map<string, UniversalDrawerFieldConfig>(
        drawerFields.flatMap((fieldConfig) =>
          getOwnedFieldKeys(fieldConfig).map((key) => [key, fieldConfig]),
        ),
      ),
    [drawerFields],
  );

  const draft = useSelector(store, (snapshot) => snapshot.context.draft);

  // One row per drawer field, at the place of the first clause it owns.
  const visibleFieldConfigs = useMemo(
    () =>
      new Set(
        draft.flatMap(({ field }) => fieldConfigByField.get(field) ?? []),
      ),
    [draft, fieldConfigByField],
  );
  const addableFieldConfigs = useMemo(
    () =>
      drawerFields.filter(
        (fieldConfig) => !visibleFieldConfigs.has(fieldConfig),
      ),
    [drawerFields, visibleFieldConfigs],
  );

  if (visibleFieldConfigs.size === 0 && addableFieldConfigs.length === 0) {
    return null;
  }

  function handleAddField(rowField: string) {
    const fieldConfig = drawerFields.find(({ field }) => field === rowField);
    if (fieldConfig === undefined) return;

    const [field] = getOwnedFieldKeys(fieldConfig);
    setPendingFocusField(rowField);
    store.trigger.editSelectedDraftClause({ field });
  }

  return (
    <div className="flex flex-col gap-4">
      {[...visibleFieldConfigs].map((fieldConfig) => {
        const ownedKeys = getOwnedFieldKeys(fieldConfig);

        return (
          <FiltersDrawerConfiguredRow
            key={fieldConfig.field}
            fieldConfig={fieldConfig}
            removeAriaLabel={t(($) => $.drawer.removeRow.ariaLabel)}
            shouldAutoFocus={pendingFocusField === fieldConfig.field}
            values={Object.fromEntries(
              draft.flatMap(({ field, value }) =>
                ownedKeys.includes(field) ? [[field, value]] : [],
              ),
            )}
            onEdit={(nextClause) => {
              store.trigger.editSelectedDraftRow({
                clause: nextClause,
                fields: ownedKeys,
              });
            }}
            onRemove={() => {
              for (const field of ownedKeys) {
                store.trigger.removeSelectedDraftClause({ field });
              }
            }}
          />
        );
      })}
      {visibleFieldConfigs.size === 0 ? (
        <FiltersDrawerAddFieldList
          addableFieldConfigs={addableFieldConfigs}
          label={t(($) => $.drawer.addFieldList.label)}
          onAddField={handleAddField}
        />
      ) : (
        <FiltersDrawerAddFieldMenu
          addableFieldConfigs={addableFieldConfigs}
          ariaLabel={t(($) => $.drawer.addField.ariaLabel)}
          onAddField={handleAddField}
        />
      )}
    </div>
  );
}
